import {Inject, Injectable} from '@angular/core';
import {Observable, ReplaySubject} from "rxjs";
import {CountedAndPaginated} from "../interface/response/counted-and-paginated";
import {ReceivablesBalance, ReceivablesBalanceNote} from "../interface/receivables-balance";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {FilterService} from "./filter.service";
import {DOCUMENT} from "@angular/common";
import {first, take} from "rxjs/operators";
import {Uuid} from "../interface/response/uuid";
import {Post} from "../interface/post";
import {Registration} from "../interface/registration";
import {ReceivablesBalanceHistory} from "../interface/receivables-balance-history";
import {ReceivablesStatement} from "../interface/receivables-statement";
import {ListResponse} from "../interface/response/list-response";
import {Ofwca} from "../interface/ofwca";
import {Contract} from "../interface/contract";

@Injectable({
  providedIn: 'root'
})
export class ReceivablesBalanceService {
  public readonly rowListSubject: ReplaySubject<CountedAndPaginated<ReceivablesBalance>>
    = new ReplaySubject<CountedAndPaginated<ReceivablesBalance>>(1);
  private readonly historyListSubject: ReplaySubject<CountedAndPaginated<ReceivablesBalanceHistory>>
    = new ReplaySubject<CountedAndPaginated<ReceivablesBalanceHistory>>(1);
  private readonly receivablesBalanceNotCountedListSubject: ReplaySubject<ListResponse<ReceivablesBalance>>
    = new ReplaySubject<ListResponse<ReceivablesBalance>>(1);


  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document
  ) { }

  public loadList(page: number, pageSize: number, filters: object, structure: string,
                  sort?: { sortBy?: string, sortOrder?: string }): void
  {
    this.httpClient.get(`/rest/crm/receivables-balance?page=${page + 1}&pageSize=${pageSize}&sortBy=${sort.sortBy ?? ''}&sortOrder=${sort.sortOrder ?? ''}&`
      + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe({
        next: (result: CountedAndPaginated<ReceivablesBalance>) => {
          this.rowListSubject.next(result);
        }, error: (error: HttpErrorResponse) => {
          this.rowListSubject.error(error);
        }
      });
  }

  public get(receivablesBalanceId: string): Observable<ReceivablesBalance> {
    return this.httpClient.get(`/rest/crm/receivables-balance/${receivablesBalanceId}`)
      .pipe(first()) as Observable<ReceivablesBalance>;
  }

  public downloadReport(filters: any): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/receivables-balance/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public downloadHistoryReport(filters: any): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/receivables-balance-history/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public loadHistoryList(page: number, pageSize: number, filters: object): void
  {
    let loadFilter: string;
    loadFilter = this.filterService.parseFilters(filters);
    this.httpClient.get(`/rest/crm/receivables-balance-history?page=${page + 1}&pageSize=${pageSize}&` + loadFilter)
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<ReceivablesBalanceHistory>) => {
        this.historyListSubject.next(result);
      });
  }
  public getHistoryList(): ReplaySubject<CountedAndPaginated<ReceivablesBalanceHistory>> {
    return this.historyListSubject;
  }

  public send(formData: object): Observable<any> {
      return this.httpClient.post(`/rest/crm/receivables-balance`, formData);
  }

  public restart( uuid: string): Observable<any> {
    return this.httpClient.put(`/rest/crm/receivables-balance/${uuid}/restart`, {});
  }

  public sendUpdateOfwca(param: {ofwca: any }, uuid: string): Observable<any> {
    return this.httpClient.put(`/rest/crm/receivables-balance/${uuid}/ofwca`, param);
  }
  public sendUpdatePartnerFacility(param: {partnerFacility: any }, uuid: string): Observable<any> {
    return this.httpClient.put(`/rest/crm/receivables-balance/${uuid}/partner-facility`, param);
  }

  public loadNotCountedList(page: number, pageSize: number, filters: object, observable?: ReplaySubject<ListResponse<ReceivablesBalance>>): void {
    this.httpClient
      .get(`/rest/crm/receivables-balance?disableCount=1&page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: ListResponse<ReceivablesBalance>) => {
        if (observable) {
          observable.next(result);
        } else {
          this.receivablesBalanceNotCountedListSubject.next(result);
        }
      });
  }

  public activeUpdate(param: {active: boolean }, uuid?: string): Observable<Uuid> {
      return this.httpClient.post(`/rest/crm/receivables-balance/${uuid}/active`, param)
        .pipe(first()) as Observable<Uuid>;

  }

  public sendEmail(uuid: string): Observable<any> {
    return this.httpClient.get( `/rest/crm/receivables-balance/${uuid}/send-statement`)
      .pipe(first()) as Observable<any>;
  }

  public sendNote(formData: object, uuid: string): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/receivables-balance/${uuid}/note`, formData)
      .pipe(first()) as Observable<Uuid>;
  }
  public getReceivablesBalanceNote(uuid: string): Observable<ListResponse<ReceivablesBalanceNote>> {
    return this.httpClient.get(`/rest/crm/receivables-balance/${uuid}/note`)
      .pipe(first()) as Observable<ListResponse<ReceivablesBalanceNote>>;
  }
}
